import { useEffect, useState } from "react";
import { etaTargetLabel } from "@/lib/tripPhase";
import { 
  Send, CheckCircle, Clock, Car, MapPin, Navigation, 
  Phone, User, Flag, Loader2
} from "lucide-react";

// Timeline stages for ride lifecycle
const RIDE_STAGES = [
  { key: "requested", label: "Requested", icon: Send, description: "Awaiting dispatch approval" },
  { key: "approved", label: "Approved", icon: CheckCircle, description: "Approved by dispatch" },
  { key: "scheduled", label: "Scheduled", icon: Clock, description: "Driver assigned" },
  { key: "in_progress", label: "En Route", icon: Car, description: "Driver on the way" },
  { key: "arrived", label: "Arrived", icon: MapPin, description: "Driver at pickup" },
  { key: "on_trip", label: "On Trip", icon: Navigation, description: "Heading to dropoff" },
  { key: "completed", label: "Completed", icon: Flag, description: "Trip completed" }
];

// Get stage index for comparison
const getStageIndex = (status) => {
  const index = RIDE_STAGES.findIndex(s => s.key === status);
  return index === -1 ? 0 : index;
};

export default function RideProgressTimeline({ 
  ride, 
  variant = "light", // "light" or "dark"
  compact = false,
  showDriver = true,
  showETA = true
}) {
  const [animatedStage, setAnimatedStage] = useState(-1);
  const currentStageIndex = ["on_trip", "waiting"].includes(ride?.trip_phase)
    ? getStageIndex("on_trip")
    : getStageIndex(ride?.status);
  
  // Animate stages when component mounts or status changes
  useEffect(() => {
    if (!ride) return;
    
    let timeoutIds = [];
    RIDE_STAGES.forEach((_, index) => {
      if (index <= currentStageIndex) {
        const id = setTimeout(() => {
          setAnimatedStage(index);
        }, index * 200);
        timeoutIds.push(id);
      }
    });
    
    return () => timeoutIds.forEach(id => clearTimeout(id));
  }, [ride?.status, currentStageIndex]);

  if (!ride) {
    return (
      <div className={`text-center py-8 ${variant === "dark" ? "text-slate-400" : "text-slate-500"}`}>
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-sm">Loading ride information...</p>
      </div>
    );
  }

  // Skip rejected rides - show simple badge
  if (ride.status === "rejected") {
    return (
      <div className={`p-4 rounded-xl text-center ${
        variant === "dark" ? "bg-red-500/20 border border-red-500/30" : "bg-red-100 border border-red-200"
      }`}>
        <p className={`font-semibold ${variant === "dark" ? "text-red-400" : "text-red-700"}`}>
          Ride Request Declined
        </p>
        <p className={`text-sm mt-1 ${variant === "dark" ? "text-red-400/70" : "text-red-600"}`}>
          Please contact dispatch for more information
        </p>
      </div>
    );
  }

  // Color schemes
  const colors = {
    light: {
      completed: "bg-gradient-to-r from-green-500 to-emerald-500",
      active: "bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse",
      pending: "bg-slate-200",
      line_completed: "bg-gradient-to-b from-green-500 to-emerald-500",
      line_pending: "bg-slate-200",
      text_completed: "text-slate-800",
      text_active: "text-blue-700",
      text_pending: "text-slate-400",
      label_completed: "text-slate-700",
      label_pending: "text-slate-400",
      bg: "bg-white",
      driver_bg: "bg-slate-50 border-slate-200"
    },
    dark: {
      completed: "bg-gradient-to-r from-lime-400 to-green-500",
      active: "bg-gradient-to-r from-cyan-400 to-blue-500 animate-pulse",
      pending: "bg-slate-700",
      line_completed: "bg-gradient-to-b from-lime-400 to-green-500",
      line_pending: "bg-slate-700",
      text_completed: "text-white",
      text_active: "text-cyan-300",
      text_pending: "text-slate-500",
      label_completed: "text-slate-200",
      label_pending: "text-slate-500",
      bg: "bg-slate-900",
      driver_bg: "bg-slate-800 border-slate-700"
    }
  };

  const c = colors[variant];

  // Compact horizontal version
  if (compact) {
    return (
      <div className={`p-4 rounded-xl ${c.bg}`} data-testid="ride-timeline-compact">
        {/* Progress bar */}
        <div className="relative h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
          <div 
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 via-cyan-500 to-blue-500 transition-all duration-700 ease-out"
            style={{ width: `${((currentStageIndex + 1) / RIDE_STAGES.length) * 100}%` }}
          />
        </div>
        
        {/* Stage indicators */}
        <div className="flex justify-between items-center">
          {RIDE_STAGES.map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isActive = index === currentStageIndex;
            const Icon = stage.icon;
            
            return (
              <div key={stage.key} className="flex flex-col items-center relative group">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isCompleted ? c.completed : isActive ? c.active : c.pending
                  } ${animatedStage >= index ? 'scale-100 opacity-100' : 'scale-75 opacity-50'}`}
                >
                  <Icon className={`w-4 h-4 ${
                    isCompleted || isActive ? "text-white" : variant === "dark" ? "text-slate-500" : "text-slate-400"
                  }`} />
                </div>
                
                {/* Tooltip */}
                <div className={`absolute -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-xs ${
                  variant === "dark" ? "text-slate-300" : "text-slate-600"
                }`}>
                  {stage.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Current status text */}
        <div className="mt-6 text-center">
          <p className={`text-sm font-medium ${
            variant === "dark" ? "text-cyan-400" : "text-blue-600"
          }`}>
            {RIDE_STAGES[currentStageIndex]?.label}: {RIDE_STAGES[currentStageIndex]?.description}
          </p>
          {showETA && ride.eta_minutes && ride.status === "in_progress" && (
            <p className={`text-lg font-bold mt-1 ${
              variant === "dark" ? "text-lime-400" : "text-green-600"
            }`}>
              ETA {etaTargetLabel(ride)}: {ride.eta_minutes} minutes
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full vertical timeline
  return (
    <div className={`p-5 rounded-2xl ${c.bg}`} data-testid="ride-timeline-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className={`font-bold text-lg ${variant === "dark" ? "text-white" : "text-slate-800"}`}>
          Ride Progress
        </h3>
        {showETA && ride.eta_minutes && ride.status === "in_progress" && (
          <div className={`px-3 py-1.5 rounded-full ${
            variant === "dark" ? "bg-lime-400/20 text-lime-400" : "bg-green-100 text-green-700"
          }`}>
            <span className="text-sm font-bold">ETA {etaTargetLabel(ride)}: {ride.eta_minutes} min</span>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {RIDE_STAGES.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isActive = index === currentStageIndex;
          const isPending = index > currentStageIndex;
          const Icon = stage.icon;
          const isLast = index === RIDE_STAGES.length - 1;
          
          return (
            <div 
              key={stage.key} 
              className={`flex items-start gap-4 ${!isLast ? 'pb-6' : ''} transition-all duration-500 ${
                animatedStage >= index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
            >
              {/* Icon and line */}
              <div className="relative flex flex-col items-center">
                {/* Circle */}
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                    isCompleted ? c.completed : isActive ? c.active : c.pending
                  } ${isActive ? 'ring-4 ring-cyan-400/30' : ''}`}
                >
                  {isActive ? (
                    <div className="relative">
                      <Icon className="w-5 h-5 text-white" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
                    </div>
                  ) : (
                    <Icon className={`w-5 h-5 ${
                      isCompleted ? "text-white" : variant === "dark" ? "text-slate-500" : "text-slate-400"
                    }`} />
                  )}
                </div>
                
                {/* Connecting line */}
                {!isLast && (
                  <div 
                    className={`w-0.5 h-full absolute top-10 ${
                      isCompleted ? c.line_completed : c.line_pending
                    }`}
                  />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <p className={`font-semibold ${
                  isCompleted ? c.label_completed : isActive ? c.text_active : c.label_pending
                }`}>
                  {stage.label}
                </p>
                <p className={`text-sm mt-0.5 ${
                  isCompleted || isActive ? (variant === "dark" ? "text-slate-400" : "text-slate-600") : c.text_pending
                }`}>
                  {stage.description}
                </p>
                
                {/* Show timestamp for completed stages (simulated) */}
                {isCompleted && (
                  <p className={`text-xs mt-1 ${
                    variant === "dark" ? "text-slate-500" : "text-slate-400"
                  }`}>
                    {new Date(Date.now() - (currentStageIndex - index) * 600000).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Driver info section */}
      {showDriver && ride.driver_name && ride.status !== "requested" && ride.status !== "approved" && (
        <div className={`mt-6 p-4 rounded-xl border ${c.driver_bg}`} data-testid="timeline-driver-info">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                variant === "dark" ? "bg-slate-700" : "bg-slate-200"
              }`}>
                {ride.driver_picture ? (
                  <img src={ride.driver_picture} alt={ride.driver_name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className={`w-6 h-6 ${variant === "dark" ? "text-slate-400" : "text-slate-500"}`} />
                )}
              </div>
              <div>
                <p className={`font-semibold ${variant === "dark" ? "text-white" : "text-slate-800"}`}>
                  {ride.driver_name}
                </p>
                {ride.driver_vehicle_make && (
                  <p className={`text-sm ${variant === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    {[ride.driver_vehicle_color, ride.driver_vehicle_make, ride.driver_vehicle_model].filter(Boolean).join(' ')}
                  </p>
                )}
                {ride.driver_vehicle_plate && (
                  <p className={`text-xs font-mono ${variant === "dark" ? "text-lime-400" : "text-blue-600"}`}>
                    {ride.driver_vehicle_plate}
                  </p>
                )}
              </div>
            </div>
            
            {ride.driver_phone && (
              <a 
                href={`tel:${ride.driver_phone}`}
                className={`p-3 rounded-full transition-colors ${
                  variant === "dark" 
                    ? "bg-lime-400/20 text-lime-400 hover:bg-lime-400/30" 
                    : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                }`}
                data-testid="timeline-call-driver"
              >
                <Phone className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
